import { FC } from 'react';
import { useTranslation } from 'react-i18next';
// Types
import QuizTypes from 'types/QuizTypes';
import QuestionTypes from 'types/QuestionTypes';
// Models
import IQuiz from 'models/Quiz';
import IQuestion from 'models/Question';
import IAnswer, { IAnswerResult } from 'models/Answer';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Actions
import { setAnswers, setQuestionEnd } from 'store/player/playerSlice';
// Selectors
import { selectQuiz, selectQuestion } from 'store/quiz/quizSelectors';
import { selectQuestionReady, selectIsAnyAnswerCorrect } from 'store/player/playerSelectors';
// Hooks
import useQuestionInterval from 'hooks/useQuestionInterval';
// 
import AnswerButton from './AnswerButton';
import TextAnswer from './TextAnswer';
import AnswerDropdown from './AnswerDropdown';
import MultiAnswer from './MultiAnswer';
import GameScoreAnswers from './ScoreAnswers';
import QuestionFormats from 'types/QuestionFormats';

const ScoringAnswers:FC = () => {
  const { t } = useTranslation('common');
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const quiz:IQuiz = useAppSelector(selectQuiz);
  const question:IQuestion = useAppSelector(selectQuestion);
  const questionReady:boolean = useAppSelector(selectQuestionReady);
  const isAnyAnswerCorrect:boolean = useAppSelector(selectIsAnyAnswerCorrect);

  const { score, time } = useQuestionInterval();

  const handleClick = ({ ref, title, correct }:{ ref:string, title:string, correct?:boolean }) => {

    const nextAnswer:IAnswerResult = {
      ref,
      title,
      score: isAnyAnswerCorrect || correct || quiz.type === QuizTypes.Prediction ? score : 0,
      time,
      question: {
        ref: question.ref,
        title: question.title
      }
    };

    if (quiz.type === QuizTypes.Scoring) {
      nextAnswer['correct'] = isAnyAnswerCorrect ? true : correct;
    }

    dispatch(setAnswers([nextAnswer]));
    dispatch(setQuestionEnd(true));
  }

  if ( quiz.type === QuizTypes.Personality ) return null;
  return (
    <div className="py-2 px-4">
      <div className="flex flex-col gap-4 mb-4">
        {question.type === QuestionTypes.ShortText && (
          question.format === QuestionFormats.Score 
            ? <GameScoreAnswers onSubmit={handleClick} disabled={!questionReady} />
            : <TextAnswer onSubmit={handleClick} disabled={!questionReady} />
        )}
        {question.type === QuestionTypes.MultiChoice && (
          <MultiAnswer answers={question.answers} disabled={!questionReady} />
        )}
        {question.type === QuestionTypes.SingleChoice && (
          question.renderAsDropdown ? (
            <AnswerDropdown answers={question.answers} onSubmit={handleClick} disabled={!questionReady} />
          ) : (
            question.answers.map((answer:IAnswer) => (
              <AnswerButton
                key={`answer-item-${answer.ref}`}
                answer={answer}
                onClick={handleClick}
                disabled={!questionReady}
              />
            ))
          )
        )}
      </div>
      {!questionReady && (
        <span className="block text-gray-500 text-md font-bold text-center">
          {question.video ? t('quiz.questionPage.readyToAnswerVideo') : t('quiz.questionPage.readyToAnswerImage')}
        </span>
      )}
    </div>
  )
}

export default ScoringAnswers;
